import secureApi from '../../utils/secureApi';
import { getImageUrl } from '../../utils/imageUrl';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Tournament interface based on the provided API response
export interface ManageTournament {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  thumbnail?: string;
  coverImage?: string; // Added for cover image functionality
  heroImage?: string;
  location: string;
  startDate: string;
  endDate: string;
  registrationStartDate?: string;
  registrationEndDate?: string;
  status: string;
  slug?: string;
  sortNo?: number;
  sports: string;
  isActive?: boolean;
  isRegistrationOpen?: boolean;
  ageCategories: string[];
  cancellationInfo?: {
    reason: string;
    date: string;
    _id: string;
  } | null;
  milestonePayment?: Array<{
    milestone: string;
    ageCategory: string;
    dueDate: string;
    totalDuePercentage: number;
  }>;
  pricing?: Array<{
    ageCategory: string;
    amount: number;
    dobLimit: string;
  }>;
  __v?: number;
}

// API response interface
export interface ManageTournamentApiResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    paginatedResults: ManageTournament[];
    totalCount: number;
  };
}

// Normalize tournament data
const normalizeManageTournament = (tournament: any): ManageTournament => {
  return {
    _id: tournament._id || '',
    name: tournament.name || '',
    shortName: tournament.shortName || '',
    logo: tournament.logo || '',
    thumbnail: tournament.thumbnail || '',
    coverImage: tournament.coverImage || '', // Added for cover image functionality
    heroImage: tournament.heroImage || '',
    location: tournament.location || '',
    startDate: tournament.startDate || '',
    endDate: tournament.endDate || '',
    registrationStartDate: tournament.registrationStartDate || '',
    registrationEndDate: tournament.registrationEndDate || '',
    status: tournament.status || 'upcoming',
    sports: tournament.sports || '',
    ageCategories: Array.isArray(tournament.ageCategories) ? tournament.ageCategories : [],
    cancellationInfo: tournament.cancellationInfo || null,
    slug: tournament.slug,
    sortNo: tournament.sortNo,
    isActive: tournament.isActive,
    isRegistrationOpen: tournament.isRegistrationOpen,
    milestonePayment: tournament.milestonePayment,
    pricing: tournament.pricing,
    __v: tournament.__v,
  };
};

/**
 * Fetch tournaments by status for manage tournaments module
 * @param status - Tournament status ('upcoming', 'past', 'deleted')
 * @param page - Page number (default: 1)
 * @param pageSize - Page size (default: 10)
 * @returns Promise<{tournaments: ManageTournament[], totalCount: number}>
 */
export const getManageTournamentsByStatus = async (
  status: string, 
  page: number = 1, 
  pageSize: number = 10
): Promise<{tournaments: ManageTournament[], totalCount: number}> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    // Use secureApi for automatic token refresh
    const response = await secureApi.get<any>(
      `/api/v2/tournaments/manage/list-tournaments`
    );
    
    const payload = Array.isArray(response) || Array.isArray(response?.paginatedResults) || Array.isArray(response?.results)
      ? response
      : response?.data;
    const tournamentRecords = Array.isArray(payload)
      ? payload
      : payload?.paginatedResults || payload?.results;

    if (!Array.isArray(tournamentRecords)) {
      throw new Error('Invalid response format from API');
    }

    const filteredTournaments = tournamentRecords.filter(
      (tournament: ManageTournament) => tournament.status === status
    );
    const startIndex = (page - 1) * pageSize;

    return {
      tournaments: filteredTournaments
        .slice(startIndex, startIndex + pageSize)
        .map(normalizeManageTournament),
      totalCount: filteredTournaments.length,
    };
  } catch (error: any) {
    console.error(`Failed to fetch ${status} tournaments:`, error);
    
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to view tournaments.');
    } else if (error.response?.status === 404) {
      // For 404 errors, return empty result instead of throwing error
      console.log(`📭 No ${status} tournaments found (404) - returning empty result`);
      return {
        tournaments: [],
        totalCount: 0
      };
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw new Error(error.message || `Failed to fetch ${status} tournaments`);
    }
  }
};

/**
 * Get upcoming tournaments for manage tournaments
 */
export const getUpcomingManageTournaments = async (page: number = 1, pageSize: number = 10) => {
  return getManageTournamentsByStatus('upcoming', page, pageSize);
};

/**
 * Get past tournaments for manage tournaments
 */
export const getPastManageTournaments = async (page: number = 1, pageSize: number = 10) => {
  return getManageTournamentsByStatus('past', page, pageSize);
};

/**
 * Get cancelled tournaments for manage tournaments
 */
export const getCancelledManageTournaments = async (page: number = 1, pageSize: number = 10) => {
  return getManageTournamentsByStatus('cancelled', page, pageSize);
};

/**
 * Get logo URL with fallback
 */
export const getTournamentLogoUrl = (tournament: ManageTournament): string | null => {
  // If logo is empty, null, or just "logo" (placeholder), return null
  if (!tournament.logo || tournament.logo === 'logo' || tournament.logo === '') {
    return null;
  }
  
  // If it's already a full URL, return as is
  if (tournament.logo.startsWith('http')) {
    return tournament.logo;
  }
  
  // Use getImageUrl utility to construct the full URL
  return getImageUrl(tournament.logo);
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  } catch (error) {
    return dateString;
  }
};

/**
 * Get age categories display string
 */
export const getAgeCategoriesDisplay = (ageCategories: string[]): string => {
  if (!Array.isArray(ageCategories) || ageCategories.length === 0) {
    return 'N/A';
  }
  
  return ageCategories.join(', ');
};

export const getTournamentById = async (tournamentId: string, _sport?: string): Promise<ManageTournament> => {
  try {
    const url = `/api/v2/tournaments/manage/find-by-id/${tournamentId}`;
    const response = await secureApi.get(url);
    
    // Handle different response structures
    let tournamentData;
    if (response && response.data && response.data._id) {
      // If response has a data property
      tournamentData = response.data;
    } else if (response && response._id) {
      // If response is the tournament data directly
      tournamentData = response;
    } else {
      console.error('❌ Invalid response format:', response);
      throw new Error('Invalid response format from API');
    }
    
    return tournamentData;
  } catch (error: any) {
    console.error('❌ Error fetching tournament details:', error);
    
    if (error.response?.status === 404) {
      throw new Error('Tournament not found');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to view this tournament.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw new Error(error.message || 'Failed to fetch tournament details');
    }
  }
};

/**
 * Get the current active tournament (for DOB validation in player forms)
 * This function tries multiple approaches to get a valid tournament with pricing data
 */
/**
 * Test function to verify the tournament API endpoint works
 * This can be called from browser console for debugging
 */
export const testTournamentAPI = async (): Promise<void> => {
  try {
    console.log('🧪 Testing tournament API endpoint...');
    const tournament = await getTournamentById('68a2eef0510ff4ab2c518dc2');
    console.log('🧪 Tournament API test result:', tournament);
    console.log('🧪 Tournament name:', tournament?.name);
    console.log('🧪 Tournament pricing data:', tournament?.pricing);
    console.log('🧪 Age categories with DOB limits:');
    tournament?.pricing?.forEach(p => {
      console.log(`   - ${p.ageCategory}: ${p.dobLimit} (${p.amount})`);
    });
  } catch (error) {
    console.error('🧪 Tournament API test failed:', error);
  }
};

export const getCurrentActiveTournament = async (): Promise<ManageTournament | null> => {
  try {
    const { tournaments } = await getAllTournamentsForAdmin(1, 50, '', 'ongoing');
    return tournaments.find((tournament) => tournament.isActive !== false) || null;
  } catch {
    return null;
  }
};

/**
 * Create a new tournament
 */
export const createTournament = async (tournamentData: {
  name: string;
  sports: string;
  shortName?: string;
  logo?: string;
  heroImage?: string;
  thumbnail?: string;
  location?: string;
  startDate?: string;
  endDate?: string;
  registrationStartDate?: string;
  registrationEndDate?: string;
  sortNo?: number;
  ageCategories?: string[];
}): Promise<ManageTournament> => {
  try {
    
    const response = await secureApi.post(`/api/v2/tournaments/admin/add`, tournamentData);
    
    
    // Handle different response structures
    let responseData;
    if (response && response.data && response.data._id) {
      // If response has a data property
      responseData = response.data;
    } else if (response && response._id) {
      // If response is the tournament data directly
      responseData = response;
    } else {
      console.error('❌ Invalid response format:', response);
      throw new Error('Invalid response format from API');
    }
    
    return responseData;
  } catch (error: any) {
    console.error('❌ Error creating tournament:', error);
    console.error('❌ Error response:', error.response?.data);
    console.error('❌ Error status:', error.response?.status);
    
    if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || 'Invalid tournament data');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to create tournaments.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw new Error(error.message || 'Failed to create tournament');
    }
  }
};

export const updateTournamentHeroImage = async (
  tournamentId: string,
  heroImage: string | null
): Promise<ManageTournament> => {
  try {
    const response = await secureApi.post<any>(
      `/api/v2/tournaments/manage/update-tournament-details/${tournamentId}`,
      { heroImage }
    );

    if (response && response.data && response.data._id) {
      return response.data;
    }
    if (response && response._id) {
      return response;
    }

    return getTournamentById(tournamentId);
  } catch (error: any) {
    console.error('Error updating tournament hero image:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to update tournament hero image');
  }
};

/**
 * Delete a tournament
 */
export const deleteTournament = async (tournamentId: string, _cancellationInfo?: { reason: string }): Promise<void> => {
  try {
    
    await secureApi.post(`/api/v2/tournaments/manage/delete-tournament/${tournamentId}`);
    
  } catch (error: any) {
    console.error('❌ Error deleting tournament:', error);
    console.error('❌ Error response:', error.response?.data);
    console.error('❌ Error status:', error.response?.status);
    
    if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || 'Invalid deletion data');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to delete tournaments.');
    } else if (error.response?.status === 404) {
      throw new Error('Tournament not found');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw new Error(error.message || 'Failed to delete tournament');
    }
  }
};

/**
 * Get all tournaments for admin management
 * @param page - Page number for pagination (default: 1)
 * @param pageSize - Number of items per page (default: 10)
 * @param search - Search term for filtering tournaments (optional)
 * @param status - Status filter for tournaments (optional)
 * @param sports - Sports filter for tournaments (optional)
 * @returns Promise with tournaments data and pagination info
 */
export const getAllTournamentsForAdmin = async (
  page: number = 1,
  pageSize: number = 10,
  search: string = '',
  status: string = '',
  sports: string = ''
): Promise<{ tournaments: ManageTournament[]; totalCount: number; totalPages: number }> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    // Build query parameters
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    });

    if (search) {
      params.append('search', search);
    }
    if (status) {
      params.append('status', status);
    }
    if (sports) {
      params.append('sport', sports); // Backend expects 'sport' (singular), not 'sports' (plural)
    }

    const response = await secureApi.get(
      `/api/v2/tournaments/admin/get-all-tournaments?${params.toString()}`
    );

    // Handle the API response
    if (response.data && response.data.paginatedResults) {
      const tournaments = response.data.paginatedResults.map(normalizeManageTournament);
      const totalCount = response.data.totalCount || 0;
      const totalPages = Math.ceil(totalCount / pageSize);

      return {
        tournaments,
        totalCount,
        totalPages
      };
    } else {
      throw new Error('Invalid API response format');
    }
  } catch (error: any) {
    console.error('❌ Error fetching all tournaments for admin:', error);
    
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to view tournaments.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw new Error(error.message || 'Failed to fetch tournaments');
    }
  }
}; 

// Tournament Season interfaces
export interface TournamentSeasonHeroImageVariants {
  web?: string | null;
  mobile?: string | null;
}

export interface TournamentSeason {
  _id: string;
  seasonType: string;
  seasonNumber: number;
  seasonLabel: string;
  seasonYearRange: string;
  location: string;
  startDate: string;
  endDate: string;
  status: string;
  heroImage?: string | null;
  detailHeroImage?: string | null;
  detailHeroImageApp?: string | null;
  matchCardBackgroundImage?: string | null;
  heroImageVariants?: TournamentSeasonHeroImageVariants;
  ageCategories: string[];
  isFeaturedOnTv?: boolean;
  showOnHomePage?: boolean;
  tvSortOrder?: number;
  tvPreviewDuration?: number | null;
  sortOrder?: number;
  tvAccessType?: 'free' | 'paid';
  recordingAccessType?: 'free' | 'paid';
  highlightAccessType?: 'free' | 'paid';
  tvPassConfig?: TvPassConfig;
  milestonePayment?: Array<{
    milestone: string;
    ageCategory: string;
    dueDate: string;
    totalDuePercentage: number;
    _id: string;
  }>;
  pricing?: Array<{
    ageCategory: string;
    amount: number;
    dobLimit: string;
  }>;
  cancellationInfo?: {
    reason: string;
    date: string;
    _id: string;
  } | null;
}

export interface TvPassConfig {
  match: {
    enabled: boolean;
    pricing?: {
      IN: {
        amount: number;
        currency: 'INR';
      };
    };
  };
  season: {
    enabled: boolean;
    pricing?: {
      IN: {
        amount: number;
        currency: 'INR';
      };
    };
  };
  tournament: {
    enabled: boolean;
  };
}

export interface TournamentSeasonsApiResponse {
  statusCode: number;
  status: string;
  message: string;
  data: TournamentSeason[];
}

const normalizeTournamentSeason = (season: TournamentSeason): TournamentSeason => ({
  ...season,
  heroImage: season.heroImage || null,
  detailHeroImage: season.detailHeroImage || null,
  detailHeroImageApp: season.detailHeroImageApp || null,
  matchCardBackgroundImage: season.matchCardBackgroundImage || null,
  heroImageVariants: {
    ...(season.heroImageVariants || {}),
    web: season.heroImageVariants?.web || null,
    mobile: season.heroImageVariants?.mobile || null,
  },
});

const getTournamentSeasonErrorMessage = (error: any, fallback: string): string => {
  const responseData = error?.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }

  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    return responseData.errors
      .map((item: any) => item?.message || item?.msg || item?.error)
      .filter(Boolean)
      .join(', ');
  }

  return (
    responseData?.message ||
    responseData?.error ||
    error?.message ||
    fallback
  );
};

/**
 * Get all tournament seasons including DEFAULT seasons
 * @param tournamentId - Tournament ID
 * @param sport - Sport type (e.g., 'Football', 'Badminton') - included as query parameter
 * @returns Promise with array of all tournament seasons (including DEFAULT)
 */
export const getAllTournamentSeasons = async (tournamentId: string, _sport?: string): Promise<TournamentSeason[]> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    const url = `/api/v2/tournaments/multi-sports/manage/tournament-season/list/${tournamentId}`;

    const response = await secureApi.get<any>(url);

    let seasonsArray: TournamentSeason[] = [];

    // Handle different response structures
    if (Array.isArray(response)) {
      seasonsArray = response;
    } else if (response && typeof response === 'object') {
      if (response.statusCode === 200 && response.status === 'success' && Array.isArray(response.data)) {
        seasonsArray = response.data;
      } else if (Array.isArray(response.data)) {
        seasonsArray = response.data;
      } else {
        seasonsArray = [];
      }
    }

    // Final validation: ensure we return an array
    if (!Array.isArray(seasonsArray)) {
      seasonsArray = [];
    }

    // Return ALL seasons including DEFAULT
    return seasonsArray.map(normalizeTournamentSeason);
  } catch (error: any) {
    console.error('Error fetching all tournament seasons:', error);
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

/**
 * Get tournament seasons list for a specific tournament (excluding DEFAULT seasons)
 * @param tournamentId - Tournament ID
 * @param sport - Sport type (e.g., 'Football', 'Badminton') - included as query parameter
 * @returns Promise with array of tournament seasons (SEASON type only)
 */
export const getTournamentSeasons = async (tournamentId: string, _sport?: string): Promise<TournamentSeason[]> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    const url = `/api/v2/tournaments/multi-sports/manage/tournament-season/list/${tournamentId}`;

    const response = await secureApi.get<any>(url);

    let seasonsArray: TournamentSeason[] = [];

    if (Array.isArray(response)) {
      seasonsArray = response;
    } else if (response && typeof response === 'object') {
      if (response.statusCode === 200 && response.status === 'success' && Array.isArray(response.data)) {
        seasonsArray = response.data;
      } else if (Array.isArray(response.data)) {
        seasonsArray = response.data;
      } else if (!response.data || (typeof response.data === 'object' && !Array.isArray(response.data))) {
        seasonsArray = [];
      }
    }

    if (!Array.isArray(seasonsArray)) {
      seasonsArray = [];
    }

    const actualSeasons = seasonsArray
      .filter((season: TournamentSeason) => {
        return season.seasonType === 'SEASON';
      })
      .map(normalizeTournamentSeason);
    
    return actualSeasons;
  } catch (error: any) {
    console.error('Error fetching tournament seasons:', error);
    
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to view tournament seasons.');
    } else if (error.response?.status === 404) {
      // Return empty array if no seasons found (404)
      return [];
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      // For any other error, return empty array to ensure tournaments without seasons
      // don't get stuck trying to show seasons listing
      return [];
    }
  }
};

/**
 * Add a new tournament season
 * @param tournamentId - Tournament ID (included in URL)
 * @param sport - Sport type (e.g., 'Football', 'Badminton') - included as query parameter
 * @param seasonData - Season data to add
 * @returns Promise with the created season
 */
export const addTournamentSeason = async (
  tournamentId: string,
  _sport: string,
  seasonData: {
    seasonType: string;
    seasonNumber: number;
    seasonLabel: string;
    seasonYearRange: string;
    location: string;
    startDate: string;
    endDate: string;
    ageCategories: string[];
    milestonePayment: any[];
    pricing: any[];
    heroImage?: string | null;
    detailHeroImage?: string | null;
    detailHeroImageApp?: string | null;
    matchCardBackgroundImage?: string | null;
    heroImageVariants?: {
      web?: string | null;
      mobile?: string | null;
    };
    isFeaturedOnTv?: boolean;
    showOnHomePage?: boolean;
    tvSortOrder?: number;
    tvPreviewDuration?: number | null;
    sortOrder?: number;
    tvAccessType?: 'free' | 'paid';
    recordingAccessType?: 'free' | 'paid';
    highlightAccessType?: 'free' | 'paid';
    tvPassConfig?: TvPassConfig;
  }
): Promise<TournamentSeason> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    const response = await secureApi.post<any>(
      `/api/v2/tournaments/multi-sports/manage/tournament-season/add/${tournamentId}`,
      seasonData
    );

    // Handle different response structures
    let season;
    if (response && response.data && response.data._id) {
      season = response.data;
    } else if (response && response._id) {
      season = response;
    } else {
      throw new Error('Invalid response format from API');
    }

    return season;
  } catch (error: any) {
    console.error('Error adding tournament season:', error);
    const apiMessage = getTournamentSeasonErrorMessage(error, 'Failed to add tournament season');
    
    if (error.response?.status === 400 || error.response?.status === 409) {
      throw new Error(apiMessage || 'Invalid season data');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to add seasons.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw new Error(apiMessage);
    }
  }
};

/**
 * Edit a tournament season
 * @param seasonId - Season ID to edit
 * @param sport - Sport type (e.g., 'Football', 'Badminton') - included as query parameter
 * @param seasonData - Season data to update
 * @returns Promise with the updated season
 */
export const editTournamentSeason = async (
  seasonId: string,
  _sport: string,
  seasonData: Partial<{
    seasonType: string;
    seasonNumber: number;
    seasonLabel: string;
    seasonYearRange: string;
    location: string;
    startDate: string;
    endDate: string;
    status: string;
    ageCategories: string[];
    milestonePayment: any[];
    pricing: any[];
    heroImage?: string | null;
    detailHeroImage?: string | null;
    detailHeroImageApp?: string | null;
    matchCardBackgroundImage?: string | null;
    heroImageVariants?: {
      web?: string | null;
      mobile?: string | null;
    };
    isFeaturedOnTv?: boolean;
    showOnHomePage?: boolean;
    tvSortOrder?: number;
    tvPreviewDuration?: number | null;
    sortOrder?: number;
    tvAccessType?: 'free' | 'paid';
    recordingAccessType?: 'free' | 'paid';
    highlightAccessType?: 'free' | 'paid';
    tvPassConfig?: TvPassConfig;
  }>
): Promise<TournamentSeason> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    const response = await secureApi.post<any>(
      `/api/v2/tournaments/multi-sports/manage/tournament-season/update/${seasonId}`,
      seasonData
    );

    // Handle different response structures
    let season;
    if (response && response.data && response.data._id) {
      season = response.data;
    } else if (response && response._id) {
      season = response;
    } else {
      throw new Error('Invalid response format from API');
    }

    return season;
  } catch (error: any) {
    console.error('Error editing tournament season:', error);
    const apiMessage = getTournamentSeasonErrorMessage(error, 'Failed to edit tournament season');
    
    if (error.response?.status === 400 || error.response?.status === 409) {
      throw new Error(apiMessage || 'Invalid season data');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to edit seasons.');
    } else if (error.response?.status === 404) {
      throw new Error('Season not found');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw new Error(apiMessage);
    }
  }
};

/**
 * Delete a tournament season
 * @param seasonId - Season ID to delete
 * @param sport - Sport type (e.g., 'Football', 'Badminton') - included as query parameter
 * @returns Promise<void>
 */
export const deleteTournamentSeason = async (seasonId: string, _sport?: string): Promise<void> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    await secureApi.post<any>(
      `/api/v2/tournaments/multi-sports/manage/tournament-season/delete/${seasonId}`
    );
  } catch (error: any) {
    console.error('Error deleting tournament season:', error);

    if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || 'Invalid deletion request');
    } else if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. You do not have permission to delete seasons.');
    } else if (error.response?.status === 404) {
      throw new Error('Season not found');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw new Error(error.message || 'Failed to delete tournament season');
    }
  }
};
